import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
  GraduationCap,
  CheckCircle2, AlertCircle, Loader2, User,
  Clock, Save
} from "lucide-react";
import { getApiUrl } from "../../config/api";
import { TERM_OPTIONS, ASSESSMENT_TYPES } from "../../constants/assessments";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface ClassItem { id: string; name: string; }
interface SubjectItem { id: string; name: string; short_name?: string | null; }
interface StudentItem { id: string; name: string; classId: string; }
interface ScoreEntry {
  studentId: string;
  subjectId: string;
  score: number | "";
  resultId?: string; // if it exists in DB
  isChanged?: boolean; // if changed locally
}

interface ExistingResult {
  id: string;
  studentId: string;
  subjectId: string;
  score: number;
  testTitle: string;
  testDate: string;
}

const RecordTestResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [subjects, setSubjects] = useState<SubjectItem[]>([]);
  const [students, setStudents] = useState<StudentItem[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<StudentItem[]>([]);
  const [examType, setExamType] = useState("");
  const [term, setTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [searchTerm, setSearchTerm] = useState("");
  const [scores, setScores] = useState<ScoreEntry[]>([]);
  const [existingResults, setExistingResults] = useState<Map<string, ExistingResult>>(new Map());
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const hasFetchedResults = useRef<string>(""); // what we've fetched

  const token = localStorage.getItem("token");
  // Assessment Type options
  const assessmentTypes = ASSESSMENT_TYPES;
  const termOptions = TERM_OPTIONS;
  // Generate year options (current year ± 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => (currentYear - 5 + i).toString());
  const abbreviateSubject = (name: string) => {
    const cleaned = name.trim().toUpperCase();
    if (cleaned === "CREATIVE AND TECHNOLOGY STUDIES") return "CTS";
    if (cleaned === "HOME ECONOMICS") return "HE";
    if (cleaned === "PHYSICAL EDUCATION") return "P.E.";
    if (cleaned === "SCIENCE PAPER 1") return "SP1";
    if (cleaned === "SCIENCE PAPER 2") return "SP2";
    return name;
  };
  const getSubjectLabel = (subject: SubjectItem) => {
    const shortName = typeof subject.short_name === "string" ? subject.short_name.trim() : "";
    return shortName || abbreviateSubject(subject.name);
  };
  // Get local storage key for current session
  const getStorageKey = useCallback(() => {
    if (!selectedClass || !examType || !selectedYear || !term) return null;
    return `assessment_scores_${selectedClass}_${term}_${examType}_${selectedYear}`;
  }, [selectedClass, examType, selectedYear, term]);
  // Save to local storage
  const saveToLocalStorage = useCallback((scoresToSave: ScoreEntry[]) => {
    const key = getStorageKey();
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(scoresToSave));
      setHasUnsavedChanges(true);
    } catch (err) {
      console.error("Error saving to local storage:", err);
    }
  }, [getStorageKey]);
  // Load from local storage
  const loadFromLocalStorage = useCallback((): ScoreEntry[] => {
    const key = getStorageKey();
    if (!key) return [];
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (err) {
      console.error("Error loading from local storage:", err);
    }
    return [];
  }, [getStorageKey]);
  // Check URL params for classId
  useEffect(() => {
    const classIdFromUrl = searchParams.get("classId");
    if (classIdFromUrl) {
      setSelectedClass(classIdFromUrl);
    }
  }, [searchParams]);
  // Fetch classes
  const fetchClasses = useCallback(async () => {
    if (!token) return;
    try {
      const role = localStorage.getItem("role");
      let classesData;

      if (role === "teacher") {
        // For teachers, get only their classes
        const dashboardRes = await axios.get(getApiUrl("teacher/dashboard"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        classesData = dashboardRes.data.classes || [];
      } else {
        // For admins, get all classes
        const classesRes = await axios.get(getApiUrl("academics/classes"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        classesData = Array.isArray(classesRes.data)
          ? classesRes.data
          : classesRes.data.classes || [];
      }

      setClasses(classesData.map((c: any) => ({ id: c.id || c._id, name: c.name })));
    } catch (err) {
      console.error("Error fetching classes:", err);
    }
  }, [token]);
  // Fetch subjects for selected class
  const fetchSubjects = useCallback(async () => {
    if (!selectedClass || !token) {
      setSubjects([]);
      return;
    }
    try {
      const subjectsRes = await axios.get(
        getApiUrl(`academics/classes/${selectedClass}/subjects`),
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const subjectsData = Array.isArray(subjectsRes.data)
        ? subjectsRes.data
        : subjectsRes.data.subjects || [];

      setSubjects(subjectsData.map((s: any) => ({
        id: String(s.id || s._id),
        name: s.name,
        short_name: s.short_name || null
      })));
    } catch (err) {
      console.error("Error fetching subjects:", err);
      setSubjects([]);
    }
  }, [selectedClass, token]);
  // Fetch students for selected class
  const fetchStudents = useCallback(async () => {
    if (!selectedClass || !token) {
      setStudents([]);
      setFilteredStudents([]);
      setScores([]);
      return;
    }
    setLoading(true);
    try {
      const role = localStorage.getItem("role");
      let studentsRes;

      if (role === "teacher") {
        studentsRes = await axios.get(getApiUrl(`teacher/classes/${selectedClass}/students`), {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        studentsRes = await axios.get(getApiUrl(`academics/classes/${selectedClass}/students`), {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      const studentsData = Array.isArray(studentsRes.data) ? studentsRes.data : [];

      if (studentsData.length === 0) {
        console.log("No students found for class:", selectedClass);
        setStudents([]);
        setFilteredStudents([]);
        setScores([]);
        return;
      }

      const mappedStudents = studentsData.map((s: any) => ({
        id: String(s.id || s._id),
        name: s.name || s.fullName || "Unknown",
        classId: selectedClass
      }));

      setStudents(mappedStudents);
      setFilteredStudents(mappedStudents);
      // Initialize scores array only if we have subjects
      if (subjects.length > 0) {
        const initialScores: ScoreEntry[] = [];
        mappedStudents.forEach((stu) => {
          subjects.forEach((sub) => {
            // Ensure IDs are strings for consistent matching
            initialScores.push({
              studentId: String(stu.id),
              subjectId: String(sub.id),
              score: "",
              isChanged: false
            });
          });
        });
        console.log("Initialized scores:", initialScores.length, "entries");
        console.log("Sample initial score:", initialScores[0]);
        setScores(initialScores);
      }
    } catch (err: any) {
      console.error("Error fetching students:", err);
      console.error("Error response:", err.response?.data);
      setStudents([]);
      setFilteredStudents([]);
      setScores([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, subjects, token]);
  // Filter students based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredStudents(students);
    } else {
      const filtered = students.filter(student =>
        student.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStudents(filtered);
    }
  }, [searchTerm, students]);
  // Fetch existing results when class, year, and exam type are selected
  const fetchExistingResults = useCallback(async () => {
    if (!selectedClass || !examType || !selectedYear || !term || !token) {
      setExistingResults(new Map());
      return;
    }

    setLoadingResults(true);
    try {
      const res = await axios.get(
        getApiUrl(`assessments/results/class/${selectedClass}?term=${encodeURIComponent(term)}&examType=${encodeURIComponent(examType)}&year=${encodeURIComponent(selectedYear)}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const results = Array.isArray(res.data) ? res.data : [];
      const resultsMap = new Map<string, ExistingResult>();

      console.log("Fetched results:", results.length, "results");
      console.log("Selected year:", selectedYear);
      if (results.length > 0) {
        console.log("Sample result:", results[0]);
      }
      // Process all results - year is already filtered by the backend
      results.forEach((r: any) => {
        // Ensure IDs are strings for consistent matching
        const studentId = String(r.studentId || "");
        const subjectId = String(r.subjectId || "");
        const key = `${studentId}-${subjectId}`;

        if (studentId && subjectId && !isNaN(Number(studentId)) && !isNaN(Number(subjectId))) {
          resultsMap.set(key, {
            id: String(r.id),
            studentId: studentId,
            subjectId: subjectId,
            score: Number(r.score),
            testTitle: r.testTitle || r.examType || examType,
            testDate: r.testDate || new Date(parseInt(selectedYear), 0, 1).toISOString()
          });
        }
      });

      console.log("Results map size:", resultsMap.size);
      console.log("Results map keys:", Array.from(resultsMap.keys()));

      if (results.length === 0) {
        // No results for this combination
        setMessage(null);
        setError(null);
      } else {
        setMessage(null);
        setError(null);
      }

      setExistingResults(resultsMap);
      // Load from local storage first, then merge with existing results
      const localScores = loadFromLocalStorage();
      // Use functional update to avoid dependency on scores
      setScores(prevScores => {
        // If scores haven't been initialized yet, return empty array
        if (prevScores.length === 0) {
          return prevScores; // be initialized by fetchStudents
        }
        // Clear all scores first, then populate with existing results or local storage
        const mergedScores: ScoreEntry[] = prevScores.map(score => {
          // Ensure IDs are strings for consistent matching
          const studentIdStr = String(score.studentId);
          const subjectIdStr = String(score.subjectId);
          const key = `${studentIdStr}-${subjectIdStr}`;

          const existing = resultsMap.get(key);
          const local = localScores.find(s =>
            String(s.studentId) === studentIdStr && String(s.subjectId) === subjectIdStr
          );
          // Debug logging for first few scores
          if (prevScores.indexOf(score) < 5) {
            console.log(`Matching score for ${key} (studentId: ${studentIdStr}, subjectId: ${subjectIdStr}):`, {
              existing: existing ? `Found (score: ${existing.score}, id: ${existing.id})` : "Not found",
              local: local ? `Found (score: ${local.score})` : "Not found",
              availableKeys: Array.from(resultsMap.keys()).slice(0, 5)
            });
          }
          // Prefer local storage if it exists, otherwise use existing result, otherwise clear
          if (local && local.score !== "") {
            const localScoreValue: number | "" = typeof local.score === "number" ? local.score : (local.score === "" ? "" : Number(local.score));
            return {
              ...score,
              score: localScoreValue,
              resultId: existing?.id,
              isChanged: true
            };
          } else if (existing) {
            return {
              ...score,
              score: existing.score,
              resultId: existing.id,
              isChanged: false
            };
          } else {
            // Clear the field if no existing result
            return {
              ...score,
              score: "" as const,
              resultId: undefined,
              isChanged: false
            };
          }
        });

        console.log("Merged scores count:", mergedScores.length);
        console.log("Scores with data:", mergedScores.filter(s => s.score !== "").length);

        return mergedScores;
      });

      setHasUnsavedChanges(localScores.length > 0);
      hasFetchedResults.current = `${selectedClass}_${term}_${examType}_${selectedYear}`;
    } catch (err) {
      console.error("Error fetching existing results:", err);
    } finally {
      setLoadingResults(false);
    }
  }, [selectedClass, examType, selectedYear, term, token, loadFromLocalStorage]);

  useEffect(() => {
    if (token) fetchClasses();
  }, [fetchClasses, token]);

  useEffect(() => {
    if (selectedClass && token) {
      fetchSubjects();
    } else {
      setSubjects([]);
    }
  }, [selectedClass, token, fetchSubjects]);

  useEffect(() => {
    if (selectedClass && subjects.length > 0) {
      fetchStudents();
    }
  }, [selectedClass, subjects.length, fetchStudents]);
  // Fetch existing results when all conditions are met and scores are initialized
  useEffect(() => {
    if (selectedClass && examType && term && students.length > 0 && subjects.length > 0 && scores.length > 0) {
      const fetchKey = `${selectedClass}_${term}_${examType}_${selectedYear}`;
      // If filters changed, clear old local storage
      if (hasFetchedResults.current !== "" && hasFetchedResults.current !== fetchKey) {
        const oldKey = hasFetchedResults.current.split('_');
        if (oldKey.length === 4) {
          const oldStorageKey = `assessment_scores_${oldKey[0]}_${oldKey[1]}_${oldKey[2]}_${oldKey[3]}`;
          localStorage.removeItem(oldStorageKey);
        }
      }
      // Only fetch if we haven't fetched for this combination yet
      if (hasFetchedResults.current !== fetchKey) {
        fetchExistingResults();
      }
    } else if (!selectedClass || !examType || !term) {
      // Reset when filters are not complete
      hasFetchedResults.current = "";
      if (scores.length === 0) {
        setExistingResults(new Map());
        setHasUnsavedChanges(false);
      }
    }
  }, [selectedClass, examType, term, selectedYear, students.length, subjects.length, scores.length, fetchExistingResults]);
  // Handle score change - save to local storage only
  const handleScoreChange = (
    studentId: string,
    subjectId: string,
    value: string,
    resultId?: string
  ) => {
    const numValue: number | "" = value === "" ? "" : Math.min(100, Math.max(0, Number(value) || 0));

    const updatedScores: ScoreEntry[] = scores.map(s =>
      s.studentId === studentId && s.subjectId === subjectId
        ? { ...s, score: numValue, resultId, isChanged: true }
        : s
    );

    setScores(updatedScores);
    setHasUnsavedChanges(true); // that there are unsaved changes
    saveToLocalStorage(updatedScores);
    // Debug log
    console.log(`Score changed: studentId=${studentId}, subjectId=${subjectId}, score=${numValue}, isChanged=true`);
  };
  // Handle keyboard navigation
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    studentIndex: number,
    subjectIndex: number
  ) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();

      let nextStudentIndex = studentIndex;
      let nextSubjectIndex = subjectIndex;

      if (e.key === "Enter") {
        // Enter moves to next row (next student, same subject)
        nextStudentIndex = (studentIndex + 1) % filteredStudents.length;
      } else if (e.key === "Tab") {
        if (e.shiftKey) {
          // Shift+Tab moves to previous cell
          if (subjectIndex > 0) {
            nextSubjectIndex = subjectIndex - 1;
          } else if (studentIndex > 0) {
            nextStudentIndex = studentIndex - 1;
            nextSubjectIndex = subjects.length - 1;
          }
        } else {
          // Tab moves to next cell
          if (subjectIndex < subjects.length - 1) {
            nextSubjectIndex = subjectIndex + 1;
          } else if (studentIndex < filteredStudents.length - 1) {
            nextStudentIndex = studentIndex + 1;
            nextSubjectIndex = 0;
          }
        }
      }
      // Focus next input
      const nextKey = `${filteredStudents[nextStudentIndex].id}-${subjects[nextSubjectIndex].id}`;
      const nextInput = inputRefs.current.get(nextKey);
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    } else if (e.key === "ArrowRight" && subjectIndex < subjects.length - 1) {
      e.preventDefault();
      const nextKey = `${filteredStudents[studentIndex].id}-${subjects[subjectIndex + 1].id}`;
      const nextInput = inputRefs.current.get(nextKey);
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    } else if (e.key === "ArrowLeft" && subjectIndex > 0) {
      e.preventDefault();
      const prevKey = `${filteredStudents[studentIndex].id}-${subjects[subjectIndex - 1].id}`;
      const prevInput = inputRefs.current.get(prevKey);
      if (prevInput) {
        prevInput.focus();
        prevInput.select();
      }
    } else if (e.key === "ArrowDown" && studentIndex < filteredStudents.length - 1) {
      e.preventDefault();
      const nextKey = `${filteredStudents[studentIndex + 1].id}-${subjects[subjectIndex].id}`;
      const nextInput = inputRefs.current.get(nextKey);
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    } else if (e.key === "ArrowUp" && studentIndex > 0) {
      e.preventDefault();
      const prevKey = `${filteredStudents[studentIndex - 1].id}-${subjects[subjectIndex].id}`;
      const prevInput = inputRefs.current.get(prevKey);
      if (prevInput) {
        prevInput.focus();
        prevInput.select();
      }
    }
  };
  // Sync all changes to database
  const handleSyncToDatabase = async () => {
    if (!selectedClass || !examType || !term || !token) {
      setError("Please select class, term, year, and assessment type.");
      return;
    }

    setIsSaving(true);
    setError(null);
    setMessage(null);

    try {
      // Debug: Log all scores before filtering
      console.log("=== SYNC TO DATABASE DEBUG ===");
      console.log("Total scores in state:", scores.length);
      console.log("Scores with values:", scores.filter(s => s.score !== "").length);
      console.log("Scores marked as changed:", scores.filter(s => s.isChanged).length);
      console.log("Sample scores:", scores.slice(0, 5).map(s => ({
        studentId: s.studentId,
        subjectId: s.subjectId,
        score: s.score,
        isChanged: s.isChanged,
        resultId: s.resultId
      })));
      // Get all changed scores OR scores with values (in case isChanged flag is missing)
      // This ensures we don't miss any entered data
      const changedScores = scores.filter(s => {
        const hasValue = s.score !== "";
        const isChanged = s.isChanged === true;
        return hasValue && (isChanged || !s.resultId); // if changed OR if it's a new entry without resultId
      });

      console.log("Changed scores to save:", changedScores.length);
      console.log("Changed scores details:", changedScores.map(s => ({
        studentId: s.studentId,
        subjectId: s.subjectId,
        score: s.score,
        resultId: s.resultId,
        isChanged: s.isChanged
      })));

      if (changedScores.length === 0) {
        setMessage("No changes to save.");
        setIsSaving(false);
        return;
      }
      // Group by create vs update
      const toCreate: Array<{ studentId: string; subjectId: string; score: number }> = [];
      const toUpdate: Array<{ resultId: string; score: number }> = [];

      changedScores.forEach((score, index) => {
        const scoreValue = typeof score.score === "number" ? score.score : parseFloat(score.score as string);
        // Allow scores of 0, but skip invalid scores
        if (isNaN(scoreValue) || scoreValue < 0 || scoreValue > 100) {
          console.warn(`[${index}] Skipping invalid score:`, score);
          return;
        }

        if (score.resultId) {
          console.log(`[${index}] Adding to UPDATE:`, { resultId: score.resultId, score: scoreValue });
          toUpdate.push({ resultId: score.resultId, score: scoreValue });
        } else {
          // Ensure IDs are strings for consistency
          const entry = {
            studentId: String(score.studentId),
            subjectId: String(score.subjectId),
            score: scoreValue
          };
          console.log(`[${index}] Adding to CREATE:`, entry);
          toCreate.push(entry);
        }
      });

      console.log(`=== FINAL COUNTS ===`);
      console.log(`To CREATE: ${toCreate.length} entries`);
      console.log(`To UPDATE: ${toUpdate.length} entries`);
      console.log(`Sample CREATE entries:`, toCreate.slice(0, 5));
      console.log(`Sample UPDATE entries:`, toUpdate.slice(0, 5));
      // Update existing results first
      const updatePromises = toUpdate.map(({ resultId, score }) =>
        axios.put(
          getApiUrl(`assessments/results/${resultId}`),
          { score },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      // Create new results using bulk endpoint
      if (toCreate.length > 0) {
        console.log("Sending bulk create request:", {
          classId: selectedClass,
          examType,
          year: selectedYear,
          scoresCount: toCreate.length,
          sampleScores: toCreate.slice(0, 3)
        });

        const bulkResponse = await axios.post(
          getApiUrl("assessments/results/bulk"),
          {
            classId: selectedClass,
            term,
            examType,
            year: selectedYear, // the selected year
            scores: toCreate
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log("Bulk create response:", bulkResponse.data);
      }

      await Promise.all(updatePromises);
      // Mark all as synced
      setScores(prev => prev.map(s => ({ ...s, isChanged: false })));
      setHasUnsavedChanges(false);
      // Clear local storage
      const key = getStorageKey();
      if (key) {
        localStorage.removeItem(key);
      }
      // Refresh existing results to get new IDs
      await fetchExistingResults();

      const totalSaved = toCreate.length + toUpdate.length;
      setMessage(`Successfully saved ${totalSaved} result(s) to database.`);
      setTimeout(() => setMessage(null), 5000);
    } catch (err: any) {
      console.error("Error syncing to database:", err);
      const errData = err.response?.data;
      console.error("Error response:", errData);
      if (errData?.details?.firstError) {
        console.error("🔴 ROOT CAUSE from DB:", errData.details.firstError);
      }
      const displayMsg = errData?.details?.firstError?.message
        ? `DB Error: ${errData.details.firstError.message}`
        : errData?.message || "Failed to save results. Please try again.";
      setError(displayMsg);
    } finally {
      setIsSaving(false);
    }
  };
  // Get score entry for a student-subject combination
  const getScoreEntry = (studentId: string, subjectId: string): ScoreEntry | undefined => {
    return scores.find(s => s.studentId === studentId && s.subjectId === subjectId);
  };
  // Count unsaved changes
  const unsavedCount = scores.filter(s => s.isChanged && s.score !== "").length;

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="page-title-section">
          <div>
            <h1>Record Assessment Results</h1>
            <p className="page-subtitle" />
          </div>
          {hasUnsavedChanges && (
            <button
              className="utopia-btn-primary h-12 px-8 flex items-center gap-2 shadow-xl shadow-teal-50"
              onClick={handleSyncToDatabase}
              disabled={isSaving || !selectedClass || !examType || !term}
            >
              {isSaving ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save
                </>
              )}
            </button>
          )}
        </div>

        <div className="utopia-page-container">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-8 border-b border-slate-100 bg-slate-50/20">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">
                Class
              </label>
              <select
                className="utopia-input h-14 text-sm font-bold"
                value={selectedClass}
                onChange={e => {
                  setSelectedClass(e.target.value);
                  setScores([]);
                  setExistingResults(new Map());
                  setHasUnsavedChanges(false);
                  setSearchTerm("");
                  hasFetchedResults.current = ""; // fetch tracking
                }}
              >
                <option value="">Select Class...</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">
                Year
              </label>
              <select
                className="utopia-input h-14 text-sm font-bold"
                value={selectedYear}
                onChange={e => {
                  setSelectedYear(e.target.value);
                  setExistingResults(new Map());
                  setHasUnsavedChanges(false);
                  hasFetchedResults.current = ""; // fetch tracking
                  // Clear scores when year changes
                  setScores(prevScores => prevScores.map(s => ({
                    ...s,
                    score: "",
                    resultId: undefined,
                    isChanged: false
                  })));
                }}
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">
                Term
              </label>
              <select
                className="utopia-input h-14 text-sm font-bold"
                value={term}
                onChange={e => {
                  setTerm(e.target.value);
                  setExistingResults(new Map());
                  setHasUnsavedChanges(false);
                  hasFetchedResults.current = "";
                  setScores(prevScores => prevScores.map(s => ({
                    ...s,
                    score: "",
                    resultId: undefined,
                    isChanged: false
                  })));
                }}
              >
                <option value="">Select Term...</option>
                {termOptions.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">
                Assessment Type
              </label>
              <select
                className="utopia-input h-14 text-sm font-bold"
                value={examType}
                onChange={e => {
                  setExamType(e.target.value);
                  setExistingResults(new Map());
                  setHasUnsavedChanges(false);
                  hasFetchedResults.current = ""; // fetch tracking
                  // Clear scores when assessment type changes
                  setScores(prevScores => prevScores.map(s => ({
                    ...s,
                    score: "",
                    resultId: undefined,
                    isChanged: false
                  })));
                }}
              >
                <option value="">Select Assessment Type...</option>
                {assessmentTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Messages */}
          {(message || error) && (
            <div className="p-8 pb-0">
              {message && (
                <div className="p-4 bg-teal-50 border border-teal-100 rounded-2xl text-teal-600 text-[10px] font-black uppercase tracking-widest flex items-center gap-3">
                  <CheckCircle2 size={16} /> {message}
                </div>
              )}
              {error && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-[10px] font-black uppercase tracking-widest flex items-center gap-3">
                  <AlertCircle size={16} /> {error}
                </div>
              )}
            </div>
          )}

          {/* Loading or Table */}
          {loading || loadingResults ? (
            <div className="p-32 text-center text-slate-200">
              <Loader2 className="animate-spin mx-auto" size={40} />
            </div>
          ) : filteredStudents.length > 0 && subjects.length > 0 && selectedClass && examType && term ? (
            <div className="overflow-x-auto">
              <div className="p-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <div className="flex items-center gap-2">
                  <Clock size={14} />
                  <span>Changes saved locally.</span>
                </div>
                {hasUnsavedChanges && (
                  <div className="flex items-center gap-2 text-amber-600 font-semibold">
                    <AlertCircle size={14} />
                    {unsavedCount} unsaved change{unsavedCount !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
              <table className="utopia-table">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-white z-10 w-64 border-r border-slate-50">
                      Student Name
                    </th>
                    {subjects.map(s => (
                      <th key={s.id} className="text-center w-[60px] min-w-[60px] max-w-[60px] px-1">
                        <span className="inline-block translate-x-[4px]">
                          {getSubjectLabel(s)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((stu, studentIndex) => {
                    const studentOriginalIndex = students.findIndex(s => s.id === stu.id);
                    return (
                      <tr key={stu.id} className="group/row">
                        <td className="sticky left-0 bg-white z-10 border-r border-slate-50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover/row:bg-teal-50 group-hover/row:text-teal-600 transition-all">
                              <User size={14} />
                            </div>
                            <span className="font-black text-slate-800 uppercase tracking-tight text-xs">
                              {stu.name}
                            </span>
                          </div>
                        </td>
                        {subjects.map((sub, subjectIndex) => {
                          const scoreEntry = getScoreEntry(stu.id, sub.id);
                          const score = scoreEntry?.score ?? "";
                          const resultId = scoreEntry?.resultId;
                          const isChanged = scoreEntry?.isChanged;
                          const inputKey = `${stu.id}-${sub.id}`;

                          return (
                            <td key={sub.id} className="p-2 relative">
                              <div className="flex items-center gap-1">
                                <input
                                  ref={(el) => {
                                    if (el) inputRefs.current.set(inputKey, el);
                                  }}
                                  type="number"
                                  className={`utopia-input w-[60px] min-w-[60px] max-w-[60px] h-[36px] min-h-[36px] max-h-[36px] px-2 text-center text-sm font-black leading-none no-spinner ${isChanged
                                      ? "bg-amber-50 border-amber-200"
                                      : "bg-slate-50/50 hover:bg-white focus:bg-white"
                                    }`}
                                  value={score}
                                  onChange={e => handleScoreChange(stu.id, sub.id, e.target.value, resultId)}
                                  onKeyDown={e => handleKeyDown(e, studentOriginalIndex, subjectIndex)}
                                  placeholder="0"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                />
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {hasUnsavedChanges && (
                <div className="p-6 border-t border-amber-100 flex justify-center items-center">
                  <button
                    className="utopia-btn-primary h-12 px-8 text-sm font-black uppercase tracking-widest shadow-2xl shadow-teal-100 flex items-center gap-3"
                    onClick={handleSyncToDatabase}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={20} />
                        Save
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="p-40 text-center text-slate-300">
              <GraduationCap size={80} className="mx-auto mb-6 opacity-5" />
              <p className="font-medium text-slate-400">
                {!selectedClass || !examType
                  ? "Please select a class, term, year, and assessment type to begin recording results."
                  : searchTerm
                    ? `No students found matching "${searchTerm}"`
                    : "No students found for this class."}
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default RecordTestResults;
